// Page-wide UI state shared between the filter bar, table headers, pagination,
// and the slide panels. Server data lives in React Query — this store only
// tracks transient screen state per PRD 11 §5.

import { create } from 'zustand';

// PRD §4.1 sortable columns. `preview` is non-sortable.
export type ManualSortKey = 'cid' | 'title' | 'grade' | 'category' | 'publishedAt';
export type SortDir = 'asc' | 'desc';

// PRD §3.4 / §3.5 — edit slide panel mode. `null` is closed; `create` opens an
// empty form; `{ mode: 'edit', cid }` opens the detail of that manual.
export type PanelTarget = null | { mode: 'create' } | { mode: 'edit'; cid: string };

interface CounselingManualPageState {
  search: string;
  areaFilter: string;
  subAreaFilter: string;
  sortKey: ManualSortKey;
  sortDir: SortDir;
  page: number;
  pageSize: number;
  panel: PanelTarget;

  // Search/filter changes reset to page 1 so the user always sees the top of
  // the filtered list (matches prototype). Sort change keeps the page intact.
  setSearch: (q: string) => void;
  setAreaFilter: (area: string) => void;
  setSubAreaFilter: (subArea: string) => void;
  toggleSort: (key: ManualSortKey) => void;
  setPage: (p: number) => void;
  setPageSize: (n: number) => void;
  openCreate: () => void;
  openEdit: (cid: string) => void;
  closePanel: () => void;
  reset: () => void;
}

// PRD §4.1 default sort: CID descending. First page, panels closed.
const INITIAL = {
  search: '',
  areaFilter: '',
  subAreaFilter: '',
  sortKey: 'cid' as const,
  sortDir: 'desc' as const,
  page: 1,
  pageSize: 20,
  panel: null,
};

export const useCounselingManualPageStore = create<CounselingManualPageState>((set) => ({
  ...INITIAL,
  setSearch: (q) => set({ search: q, page: 1 }),
  // Selecting an area resets the sub-area filter (2-stage cascade, PRD §3.2).
  setAreaFilter: (area) => set({ areaFilter: area, subAreaFilter: '', page: 1 }),
  setSubAreaFilter: (subArea) => set({ subAreaFilter: subArea, page: 1 }),
  toggleSort: (key) =>
    set((s) =>
      s.sortKey === key
        ? { sortDir: s.sortDir === 'asc' ? 'desc' : 'asc' }
        : { sortKey: key, sortDir: 'asc' },
    ),
  setPage: (p) => set({ page: Math.max(1, p) }),
  // Changing page size resets to page 1 (the current offset may no longer exist).
  setPageSize: (n) => set({ pageSize: Math.max(1, n), page: 1 }),
  openCreate: () => set({ panel: { mode: 'create' } }),
  openEdit: (cid) => set({ panel: { mode: 'edit', cid } }),
  closePanel: () => set({ panel: null }),
  reset: () => set(INITIAL),
}));
